import {environmentConfig} from "@/config/enviromentConfig";
import axios from "axios";
import {heLog} from "@/functions/heDebugLogger";

const SAFARICOM_IDENTITY_URL = 'https://identity.safaricom.com/partner/api/v2/fetchMaskedMsisdn';
const headers = {Authorization: `Basic ${environmentConfig.safaricomAuthHeader}`};

export default class SafaricomService {
    static async getToken() {
        heLog('→ getToken: calling /get-token...');
        try {
            const response = await axios.get(`${environmentConfig.safaricomService}/get-token`, {headers});
            const token = response.data;
            heLog('← getToken: OK', `token=${token ? token.substring(0, 20) + '...' : 'null'}`);
            return token;
        } catch (error) {
            heLog('← getToken: FAILED', error.message, true);
            throw error;
        }
    }

    static logHeAttempt({result, errorMessage, httpStatus, msisdn, durationMs}) {
        try {
            axios.post(`${environmentConfig.safaricomService}/log-he-attempt`, {
                product: 'fantasy',
                result,
                errorMessage: errorMessage || undefined,
                httpStatus: httpStatus || undefined,
                msisdn: msisdn || undefined,
                userAgent: navigator.userAgent,
                durationMs,
            }, {headers}).catch(() => {});
        } catch (_) {}
    }

    static async getMsisdnViaHeaderEnrichment() {
        heLog('→ HE: starting header enrichment...');
        const t0 = Date.now();
        try {
            const token = await SafaricomService.getToken();
            if (!token) {
                heLog('HE: aborting — no token', null, true);
                SafaricomService.logHeAttempt({result: 'no_token', durationMs: Date.now() - t0});
                return {error: true};
            }

            heLog('→ HE: calling identity.safaricom.com...');
            const response = await fetch(SAFARICOM_IDENTITY_URL, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'X-Source-System': 'he-partner',
                    'x-messageid': crypto.randomUUID(),
                    'X-App': 'he-partner',
                },
            });

            heLog('← HE: fetch response', `status=${response.status} ok=${response.ok}`);

            const data = await response.json();
            heLog('← HE: response body', data, !response.ok);

            if (!response.ok) {
                heLog('HE: non-2xx response', data?.header?.customerMessage, true);
                SafaricomService.logHeAttempt({result: 'non_2xx', httpStatus: response.status, errorMessage: data?.header?.customerMessage, durationMs: Date.now() - t0});
                return {error: true};
            }

            const msisdn = data?.ServiceResponse?.ResponseBody?.Response?.Msisdn;
            if (msisdn) {
                heLog('← HE: MSISDN resolved ✓', msisdn);
                SafaricomService.logHeAttempt({result: 'success', msisdn: String(msisdn), durationMs: Date.now() - t0});
                return {msisdn};
            }

            heLog('HE: no MSISDN in response', data?.header?.customerMessage, true);
            SafaricomService.logHeAttempt({result: 'no_msisdn', errorMessage: data?.header?.customerMessage, durationMs: Date.now() - t0});
            return {error: true};
        } catch (error) {
            heLog('← HE: fetch failed (likely CORS/whitelist)', error.message, true);
            SafaricomService.logHeAttempt({result: 'fetch_error', errorMessage: error.message, durationMs: Date.now() - t0});
            return {error: true};
        }
    }

    static async updateUsername(msisdn, username, productId) {
        return await axios.post(`${environmentConfig.safaricomService}/update-username`, {
            msisdn: msisdn,
            username: username,
            productId: productId
        }, {headers})
    }

    static async getUser(msisdn, productId, serviceId) {
        const body = serviceId != null
            ? { msisdn, serviceId }
            : { msisdn, productId };
        return await axios.post(`${environmentConfig.safaricomService}/get-user`, body, {headers})
    }

    // DIY Platform Query Active Subscriptions — returns { header, body: [...] }.
    // A non-empty body means the msisdn has an active DIY subscription with Safaricom.
    static async queryActiveSubscription(msisdn) {
        return await axios.get(`${environmentConfig.safaricomService}/query-active-subscription`, {
            params: {msisdn: msisdn},
            headers,
        })
    }

    static async requestOtp(msisdn, productId) {
        return await axios.post(`${environmentConfig.safaricomService}/request-otp`, {
                msisdn: msisdn,
                productId: productId,
            },
            {headers},
        )
    }

    static async validateOtp(msisdn, productId, otp) {
        return await axios.post(`${environmentConfig.safaricomService}/validate-otp`, {
            msisdn: msisdn,
            productId: productId,
            otp: otp,
        }, {headers})
    }

    static async unsubscribe(requestId, channel, msisdn, offerCode) {
        return await axios.post(`${environmentConfig.safaricomService}/unsubscribe`, {
            requestId,
            channel,
            msisdn,
            offerCode
        }, {headers})
    }
}
